import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { useAuth } from '@/lib/auth-context';
import { getLookbook, pauseLookbook, resumeLookbook, unpublishLookbook, rotateShareToken, patchLookbookSettings, BACKEND_URL } from '@/lib/lookbook-api';
import { patchEventCobranding } from '@/lib/events-api';
import PostPublishUpsell from '@/components/PostPublishUpsell';
import EventTeamAccessCard from '@/components/EventTeamAccessCard';
import { ArrowLeft, Copy, Briefcase } from 'lucide-react';

const POLICIES = ['PUBLIC', 'PRIVATE_LINK', 'INVITE_ONLY', 'PASSWORD', 'ATTENDEES_ONLY'];

export default function HostLookbookLivePage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';

  const [data, setData] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [cobrandBusy, setCobrandBusy] = useState(false);

  const isProfessional = (user?.account_type || '').toUpperCase() === 'PROFESSIONAL';
  const isCoHost = localStorage.getItem('activeEventViewerRole') === 'CO_HOST';

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try { const body = await getLookbook(eventId); setData(body); setError(null); }
    catch (e) { setError(e?.message || 'Failed to load lookbook.'); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  const manifest = data?.manifest ?? null;
  const eventObj = data?.event ?? {};
  const cobranding = manifest?.cobranding ?? null;

  async function toggle(field, value) {
    if (!eventId) return; setBusy(true);
    try { const res = await patchLookbookSettings(eventId, { [field]: value }); setData(p => p ? { ...p, manifest: res.manifest } : p); }
    finally { setBusy(false); }
  }
  async function changePolicy(next) {
    if (!eventId) return;
    if (next === 'PASSWORD' && !manifest?.has_password && password.length < 4) { window.alert('Enter a password (min 4 chars) before switching.'); return; }
    setBusy(true);
    try { const body = { access_policy: next }; if (next === 'PASSWORD' && password.length >= 4) body.password = password; const res = await patchLookbookSettings(eventId, body); setData(p => p ? { ...p, manifest: res.manifest } : p); }
    finally { setBusy(false); }
  }
  async function onRotate() {
    if (!eventId) return; setBusy(true);
    try { const res = await rotateShareToken(eventId); setShareToken(res.share_token); setData(p => p ? { ...p, manifest: res.manifest } : p); window.alert(`New share token:\n\n${res.share_token}`); }
    catch (e) { window.alert(e?.message || 'Failed to regenerate share token.'); }
    finally { setBusy(false); }
  }
  async function onPause() { if (!eventId) return; setBusy(true); try { const res = await pauseLookbook(eventId); setData(p => p ? { ...p, manifest: res.manifest } : p); } finally { setBusy(false); } }
  async function onResume() { if (!eventId) return; setBusy(true); try { const res = await resumeLookbook(eventId); setData(p => p ? { ...p, manifest: res.manifest } : p); } finally { setBusy(false); } }
  async function onUnpublish() { if (!eventId || !window.confirm('Unpublish? Existing links stop working.')) return; setBusy(true); try { const res = await unpublishLookbook(eventId); setData(p => p ? { ...p, manifest: res.manifest } : p); } finally { setBusy(false); } }

  async function toggleCobranding(enabled) {
    if (!eventId) return; setCobrandBusy(true);
    try {
      const res = await patchEventCobranding(eventId, enabled);
      setData(p => p ? { ...p, event: { ...p.event, ...(res.event || {}) } } : p);
    } catch (e) { window.alert(e?.message || 'Could not update co-branding.'); }
    finally { setCobrandBusy(false); }
  }

  // Phase 13a — canonical share URL: {origin}/api/v1/s/{share_token}
  const shareLink = shareToken ? `${BACKEND_URL}/api/v1/s/${shareToken}` : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="host-lookbook-live-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="lookbook-live-back">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-4">Manage Lookbook</h1>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="lookbook-live-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]">{error}</div>
      ) : !manifest ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6" data-testid="lookbook-live-none">
          <p className="font-bold text-[#0B1B46]">No manifest published yet.</p>
          <p className="text-sm text-[#5B6472] mt-1">Go to the builder and launch first.</p>
          <button onClick={() => navigate(`/host-lookbook?event_id=${encodeURIComponent(eventId)}`, { replace: true })} className="h-10 px-6 mt-3 bg-[#146BFF] text-white font-semibold rounded-lg" data-testid="lookbook-live-open-builder">Open builder</button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-live-status">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${manifest.state === 'LIVE' ? 'bg-[#E4F4EC] text-[#1F8A55]' : manifest.state === 'PAUSED' ? 'bg-[#FBEEDA] text-[#B86500]' : 'bg-[#FBE5E5] text-[#C93434]'}`}>{manifest.state}</span>
              <span className="font-bold text-[#0B1B46]">v{manifest.version} &middot; {manifest.access_policy.replace(/_/g, ' ').toLowerCase()}</span>
            </div>
            <h2 className="text-lg font-extrabold text-[#0B1B46]" data-testid="lookbook-live-title">{manifest.title}</h2>
            <p className="text-xs text-[#5B6472]">Published {manifest.published_at ? new Date(manifest.published_at).toLocaleString() : '\u2014'}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.photo_count} photos</span>
              <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.video_count} videos</span>
              <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.contributor_count} contributors</span>
            </div>
          </div>

          {/* Post-publish upsell for FREE tier */}
          <PostPublishUpsell
            eventId={eventId}
            eventCategory={data?.event_category || eventObj?.category}
            premiumTier={data?.event_premium_tier || eventObj?.premium_tier || 'FREE'}
            lookbookState={manifest?.state}
          />

          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-live-share-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Share link</p>
            {shareLink ? (
              <>
                <p className="text-xs font-mono text-[#0B1B46] bg-[#F3F5F8] p-3 rounded-lg break-all" data-testid="lookbook-live-share-link">{shareLink}</p>
                <button onClick={async () => { try { await navigator.clipboard.writeText(shareLink); window.alert('Copied!'); } catch (_e) { /* clipboard API unavailable */ } }}
                  className="h-10 mt-2 w-full flex items-center justify-center gap-2 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg text-sm" data-testid="lookbook-live-copy-link"><Copy className="w-4 h-4" /> Copy link</button>
              </>
            ) : (
              <p className="text-sm text-[#5B6472]">Share token shown only after publish or rotation. Tap &ldquo;Regenerate&rdquo; below.</p>
            )}
            <button onClick={onRotate} disabled={busy} className="h-10 mt-2 w-full bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="lookbook-live-rotate-btn">Regenerate share token</button>

            {/* OG share card preview */}
            {shareLink && (
              <div className="mt-3 p-3 bg-[#F3F5F8] rounded-lg" data-testid="lookbook-live-og-preview">
                <p className="text-[10px] font-extrabold text-[#5B6472] tracking-widest uppercase mb-2">Share preview</p>
                <p className="text-xs text-[#5B6472] mb-2">This is what recipients see when the link is shared on social media.</p>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/v1/lookbooks/${encodeURIComponent(shareToken || '')}/og-image.png`}
                  alt="Share card preview"
                  className="w-full rounded-lg border border-[#E3E8EF]"
                  onError={e => { e.target.style.display = 'none'; }}
                  data-testid="lookbook-live-og-image"
                />
              </div>
            )}
          </div>

          {/* Phase 13b — Co-branding toggle (PROFESSIONAL owner only — hidden for co-hosts) */}
          {isProfessional && !isCoHost && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-live-cobrand-card">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4 text-[#146BFF]" />
                <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Co-branding</p>
              </div>
              <p className="text-xs text-[#5B6472] mb-3">Show your business credit on the published lookbook.</p>
              <CobrandToggle
                label="Show business credit"
                value={cobranding?.enabled ?? false}
                onChange={v => toggleCobranding(v)}
                busy={cobrandBusy}
                testId="lookbook-live-cobrand-toggle"
              />
              {cobranding?.enabled && cobranding?.display_name && (
                <div className="mt-2 flex items-center gap-2 bg-[#F3F5F8] rounded-lg p-2.5" data-testid="lookbook-live-cobrand-snapshot">
                  {cobranding.logo_url && (
                    <img src={cobranding.logo_url.startsWith('http') ? cobranding.logo_url : `${BACKEND_URL.replace('/api', '')}${cobranding.logo_url}`}
                      alt="" className="w-6 h-6 rounded object-contain bg-white" />
                  )}
                  <p className="text-xs text-[#0B1B46]">Current lookbook shows credit for <span className="font-bold">{cobranding.display_name}</span></p>
                </div>
              )}
              {isProfessional && !user?.business_display_name && (
                <p className="text-[11px] text-[#5B6472] mt-2">Set up your business profile in <button onClick={() => navigate('/settings')} className="text-[#146BFF] font-bold hover:underline">Settings</button> first.</p>
              )}
            </div>
          )}

          {/* Phase 15 — Per-event team access */}
          {isProfessional && eventId && <EventTeamAccessCard eventId={eventId} />}

          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-live-access-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Access policy</p>
            <div className="flex flex-wrap gap-2">
              {POLICIES.map(p => (
                <button key={p} onClick={() => changePolicy(p)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${manifest.access_policy === p ? 'bg-[#146BFF] text-white border-[#146BFF]' : 'bg-white text-[#146BFF] border-[#146BFF]'}`}
                  data-testid={`lookbook-live-policy-${p}`}>{p.replace(/_/g, ' ').toLowerCase()}</button>
              ))}
            </div>
            {manifest.access_policy === 'PASSWORD' && (
              <div className="mt-3 flex gap-2">
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Change password"
                  className="flex-1 h-10 px-4 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="lookbook-live-password-input" />
                <button onClick={() => changePolicy('PASSWORD')} disabled={password.length < 4}
                  className="h-10 px-4 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="lookbook-live-password-save">Save</button>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-live-perms-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Viewer permissions</p>
            <Toggle label="Allow Downloads" value={manifest.allow_downloads} onChange={v => toggle('allow_downloads', v)} testId="lookbook-live-toggle-downloads" />
            <Toggle label="Allow Sharing" value={manifest.allow_sharing} onChange={v => toggle('allow_sharing', v)} testId="lookbook-live-toggle-sharing" />
            <Toggle label="Show Contributor Names" value={manifest.show_contributor_names} onChange={v => toggle('show_contributor_names', v)} testId="lookbook-live-toggle-names" />
            <Toggle label="Notify Attendees" value={!!manifest.notify_attendees} onChange={v => toggle('notify_attendees', v)} testId="lookbook-live-toggle-notify" />
          </div>

          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 flex flex-col gap-2" data-testid="lookbook-live-state-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-1">Lifecycle</p>
            {manifest.state === 'LIVE' && <button onClick={onPause} disabled={busy} className="h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg disabled:opacity-50" data-testid="lookbook-live-pause-btn">Pause</button>}
            {manifest.state === 'PAUSED' && <button onClick={onResume} disabled={busy} className="h-10 bg-[#146BFF] text-white font-semibold rounded-lg disabled:opacity-50" data-testid="lookbook-live-resume-btn">Resume</button>}
            {manifest.state !== 'UNPUBLISHED' && <button onClick={onUnpublish} disabled={busy} className="h-10 bg-[#C93434] hover:bg-[#a02a2a] text-white font-semibold rounded-lg disabled:opacity-50" data-testid="lookbook-live-unpublish-btn">Unpublish</button>}
            <button onClick={() => navigate(`/host-lookbook?event_id=${encodeURIComponent(eventId)}`)} className="h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg" data-testid="lookbook-live-open-builder">Open builder / edit</button>
            <button onClick={() => navigate(`/host-lookbook-launch?event_id=${encodeURIComponent(eventId)}`)} disabled={busy} className="h-10 bg-[#146BFF] text-white font-semibold rounded-lg disabled:opacity-50" data-testid="lookbook-live-republish-btn">Republish new version</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#E3E8EF]">
      <span className="font-bold text-[#0B1B46]">{label}</span>
      <button onClick={() => onChange(!value)} className={`w-10 h-6 rounded-full transition-colors ${value ? 'bg-[#146BFF]' : 'bg-[#E3E8EF]'}`} data-testid={testId}>
        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

function CobrandToggle({ label, value, onChange, busy, testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-bold text-[#0B1B46] text-sm">{label}</span>
      <button onClick={() => onChange(!value)} disabled={busy}
        className={`w-10 h-6 rounded-full transition-colors ${value ? 'bg-[#146BFF]' : 'bg-[#E3E8EF]'} disabled:opacity-50`} data-testid={testId}>
        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}
